// The programmatic API — what `require('flowrunner-cli')` gives you.
//
// Importing this must have no side effects: no argv parsing, no output, no process exit. The command-line
// program lives in `cli/index.ts` and is reached through `bin/flowrunner.js`, never through here. Anything
// exported below is callable from a Node program that wants to do what the CLI does without being a CLI.

// ─── Failures ────────────────────────────────────────────────────────────────
// Every operation reports problems by throwing one of these. Branch on `error.code`, or catch `CliError`
// for the lot.

export type { ClusterName, ProviderLayout } from './constants'
export {
  AI_INSTRUCTIONS_DIRECTORY,
  CLAUDE_PROVIDER,
  CLUSTERS,
  DEFAULT_CLUSTER,
  DEFAULT_SERVER_URL,
  PROVIDERS,
  SANDBOX_DIRECTORY,
  TEST_DEV_DEPENDENCIES,
  TEST_SCRIPTS,
  TESTING_FILES,
} from './constants'
export type { CliErrorCode, SkippedService } from './errors'
export {
  AiDocsMissingError,
  CliError,
  DeployFailedError,
  ERROR_CODES,
  HttpError,
  LoginFailedError,
  LoginTimeoutError,
  NoServicesDirectoryError,
  NoServicesError,
  NotAProjectError,
  NotLoggedInError,
  NoWorkspaceError,
  PromptAbortedError,
  PromptRequiredError,
  SelectionAbortedError,
  TemplateNotFoundError,
  TokenExpiredError,
  UnknownServerError,
} from './errors'

// ─── Operations ──────────────────────────────────────────────────────────────
// Each one takes `{ cwd, onEvent }`, returns what it did, and throws one of the errors above. None of them
// print, prompt or exit — supply `onEvent` to watch progress.

export type {
  DeployedService,
  DeployEvent,
  DeployOptions,
  DeployResult,
  DiscoveredService,
  DiscoveryResult,
  PlannedService,
  PublicFile,
  Selection,
  ServiceStatus,
  Workspace,
} from './operations/deploy'
export {
  collectPublicFiles,
  computeServiceHash,
  createServiceZip,
  deploy,
  discoverServices,
  selectServices,
} from './operations/deploy'
export type {
  InitEvent,
  InitOptions,
  InitResult,
  TargetDescription,
  TestingFilesResult,
} from './operations/init'
export { describeTarget, init, testingFilesDirectory, writeTestingFiles } from './operations/init'
export type { Ask, AskRequest, ResolveAnswersOptions } from './operations/init/answers'
export { resolveAnswers } from './operations/init/answers'
export type {
  CreateServiceEvent,
  CreateServiceOptions,
  ScaffoldedService,
} from './operations/init/create-service'
export { createService, resolveTemplateId, scaffoldFromTemplate } from './operations/init/create-service'
export type {
  InitClaudeEvent,
  InitClaudeOptions,
  InitClaudeResult,
  MergeResult,
  SegmentOutcome,
} from './operations/init-claude'
export {
  BEGIN_MARKER,
  END_MARKER,
  initClaude,
  mergeSegment,
  providerDirectory,
} from './operations/init-claude'
export type { LoginEvent, LoginOptions, LoginResult } from './operations/login'
export { login } from './operations/login'
export type { LogoutEvent, LogoutOptions, LogoutResult } from './operations/logout'
export { logout } from './operations/logout'
export type {
  PulledService,
  PullEvent,
  PullOptions,
  PullResult,
} from './operations/pull'
export { declaredDependencies, extractService, isSafeServiceId, localState, pull } from './operations/pull'
export { CLUSTER_NAMES, clusterFor, normalizeServerUrl, resolveServerTarget } from './operations/server-target'
export type { OperationOptions } from './operations/types'
export type { UseServerResult } from './operations/use-server'
export { useServer } from './operations/use-server'

// ─── Project state ───────────────────────────────────────────────────────────

export type { AuthGap, AuthToSave, StoredAuth } from './project/auth'
export { authGap, clearAuth, describeWorkspace, loadAuth, requireAuth } from './project/auth'
export { getServerUrl, getWorkspaceId } from './project/config'

// ─── Service templates ───────────────────────────────────────────────────────

export type { Answers, Prompt, Template, TemplateManifest } from './templates'
export { defaultTemplate, findTemplate, listTemplates } from './templates'
